import {
  NIVEIS_DE_BLINDAGEM,
  type NivelDeBlindagem,
  ROTULO_DO_CAMBIO,
  ROTULO_DO_NIVEL_DE_BLINDAGEM,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import { Car, CircleSlash, EllipsisVertical, Eye, Layers, Pencil, Plus, Search } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  type Categoria,
  type FiltroDeAtivo,
  useCategoria,
  useCategorias,
  useRemoverCategoria,
} from '@/features/frota/api'
import { BadgeDeNivelDeBlindagem, BadgeDeStatusDoVeiculo } from '@/features/frota/badges'
import { DialogoDeCategoria, formatarPontosBase } from '@/features/frota/dialogo-de-categoria'
import { ApiError, errorMessage } from '@/lib/api'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { useValorAtrasado } from '@/lib/hooks'
import { usePermissao } from '@/lib/permissoes'
import { cn } from '@/lib/utils'

/**
 * Categorias — o catálogo que o cliente realmente aluga.
 *
 * ---------------------------------------------------------------------------
 * A TELA PRECISA DEIXAR A REGRA DE OURO ÓBVIA
 * ---------------------------------------------------------------------------
 * O cliente aluga CATEGORIA, nunca modelo. Por isso `modelosRepresentativos`
 * tem coluna própria e destaque tipográfico: é a promessa que ele lê antes de
 * reservar ("Compass, Renegade ou similar") e a única coisa nesta tabela que
 * fala com o cliente e não com a operação. Caução, franquia de km e requisitos
 * do condutor vêm logo depois porque é o que ele negocia; a placa não aparece
 * aqui em lugar nenhum — o carro só é escolhido na alocação.
 *
 * ---------------------------------------------------------------------------
 * "TIRAR DO CATÁLOGO" NÃO APAGA
 * ---------------------------------------------------------------------------
 * O `DELETE` da API é desativação (`ativo = false`), e com veículo vinculado
 * ele RECUSA com 409 — a mensagem do servidor diz quantos veículos impedem. A
 * tela repete essa mensagem em vez de escrever a própria: só o servidor sabe o
 * número, e um texto genérico ("existem veículos vinculados") transformaria uma
 * instrução exata numa adivinhação.
 *
 * A ordem da listagem é a da VITRINE (`ordemDeExibicao`, depois nome) e vem
 * pronta do servidor — a mesma que o app mostra. A ordenação por cabeçalho é
 * client-side e vale só para a página carregada, então nenhuma coluna começa
 * ordenada: o estado inicial da tabela é o que o cliente vê no app.
 */

const TAMANHO_DA_PAGINA = 20

/** Filtro de três estados. `''` traz as duas; ver `FiltroDeAtivo` em `features/frota/api`. */
const OPCOES_DE_ATIVO = ['true', 'false'] as const satisfies readonly FiltroDeAtivo[]

type OpcaoDeAtivo = (typeof OPCOES_DE_ATIVO)[number]

/**
 * "Estar no catálogo" é o status da categoria, e ele aparece duas vezes nesta
 * tela: no selo da linha e no filtro de situação. O mapa existe para que as
 * duas não possam divergir — com o texto repetido à mão, o dia em que alguém
 * trocar "Fora do catálogo" por "Inativa" só muda um dos dois lugares.
 *
 * A chave é a string que o filtro manda na query, e não um enum do
 * `@locaveri/shared`, porque no contrato `ativo` é booleano: não existe enum a
 * herdar aqui.
 */
const ROTULO_DO_CATALOGO: Readonly<Record<OpcaoDeAtivo, string>> = {
  true: 'No catálogo',
  false: 'Fora do catálogo',
}

/**
 * Estar ou não no catálogo é o "status" de uma categoria — e status nunca
 * depende só de cor: cor + ícone + rótulo, como todo selo do painel.
 */
function BadgeDeCatalogo({ ativo }: { readonly ativo: boolean }): ReactElement {
  return ativo ? (
    <Badge variant="success">
      <Eye aria-hidden="true" />
      {ROTULO_DO_CATALOGO.true}
    </Badge>
  ) : (
    <Badge variant="neutral" title="Não aparece no app nem na vitrine">
      <CircleSlash aria-hidden="true" />
      {ROTULO_DO_CATALOGO.false}
    </Badge>
  )
}

/** Wire ilegível não pode derrubar a tabela inteira só porque alguém ordenou. */
function paraBigInt(texto: string): bigint {
  try {
    return BigInt(texto)
  } catch {
    return 0n
  }
}

/**
 * Ordenação de coluna monetária.
 *
 * O valor chega como STRING de centavos, e comparar string ordena
 * lexicograficamente: `"9000"` (R$ 90,00) viria DEPOIS de `"150000"`
 * (R$ 1.500,00). A comparação é feita em `bigint`, que é exato e não passa por
 * `number` — a invariante nº 1 vale também para ordenar.
 */
function compararDinheiroDoWire(a: string, b: string): number {
  const valorA = paraBigInt(a)
  const valorB = paraBigInt(b)

  return valorA === valorB ? 0 : valorA < valorB ? -1 : 1
}

/** As premissas de depreciação em uma linha. Vazio HERDA da política, não é zero. */
function textoDaDepreciacao(categoria: Categoria): string {
  const vida =
    categoria.vidaUtilEmMeses === null
      ? 'vida útil da política'
      : `${String(categoria.vidaUtilEmMeses)} meses de vida útil`

  const residual =
    categoria.residualEmPontosBase === null
      ? 'residual da política'
      : `residual de ${formatarPontosBase(categoria.residualEmPontosBase)}`

  return `${vida} · ${residual}`
}

interface DialogoDaFrotaProps {
  /** `null` mantém o diálogo fechado. */
  readonly categoria: Categoria | null
  readonly aoFechar: () => void
}

/**
 * A distribuição da frota da categoria, por status.
 *
 * Responde a pergunta que a listagem não responde: "tenho oito SUV III-A, mas
 * quantos estão de pé?". Só o DETALHE da API traz `porStatus` — e ele vem com
 * TODOS os status, os zerados inclusive, porque "nenhum carro em manutenção" é
 * exatamente a informação que o gestor quer ver escrita.
 */
function DialogoDaFrotaDaCategoria({ categoria, aoFechar }: DialogoDaFrotaProps): ReactElement {
  const consulta = useCategoria(categoria?.id ?? null)
  const detalhe = consulta.data

  return (
    <Dialog
      open={categoria !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{categoria === null ? 'Frota da categoria' : categoria.nome}</DialogTitle>
          <DialogDescription>
            {categoria === null
              ? 'Selecione uma categoria.'
              : `${categoria.modelosRepresentativos} · ${textoDaDepreciacao(categoria)}`}
          </DialogDescription>
        </DialogHeader>

        {consulta.isPending && categoria !== null ? (
          <LoadingState rotulo="Carregando a frota da categoria…" linhas={2} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar a frota"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : detalhe === undefined ? (
          // Sem dado, sem erro e sem carga em curso não é um estado que a
          // consulta produza — mas se produzisse, `null` deixaria o corpo do
          // diálogo em branco, que é exatamente o desfecho que a DoD proíbe.
          <LoadingState rotulo="Carregando a frota da categoria…" linhas={2} />
        ) : detalhe.veiculos === 0 ? (
          <EmptyState
            icone={Car}
            titulo="Nenhum veículo nesta categoria"
            descricao="Enquanto não houver carro vinculado, a categoria aparece no catálogo mas nenhuma reserva pode ser alocada."
          />
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-fg-secondary">
              <span className="numeric-display text-foreground">{detalhe.veiculos}</span>{' '}
              {detalhe.veiculos === 1 ? 'veículo' : 'veículos'} nesta categoria, em qualquer status.
            </p>

            <ul className="grid gap-2 sm:grid-cols-2">
              {detalhe.porStatus.map((linha) => (
                <li
                  key={linha.status}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-lg border border-border',
                    'bg-surface px-3 py-2',
                    linha.quantidade === 0 ? 'opacity-60' : null,
                  )}
                >
                  <BadgeDeStatusDoVeiculo status={linha.status} />
                  <span
                    className={cn(
                      'numeric-display text-sm',
                      linha.quantidade === 0 ? 'text-fg-muted' : 'text-foreground',
                    )}
                  >
                    {linha.quantidade}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function CategoriasPage(): ReactElement {
  const podeCriar = usePermissao('vehicle_category.create')
  const podeEditar = usePermissao('vehicle_category.update')
  const podeRemover = usePermissao('vehicle_category.delete')

  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState('')
  const [nivelDeBlindagem, setNivelDeBlindagem] = useState<NivelDeBlindagem | ''>('')
  const [ativo, setAtivo] = useState<FiltroDeAtivo>('')
  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Categoria | undefined>(undefined)
  const [categoriaDaFrota, setCategoriaDaFrota] = useState<Categoria | null>(null)

  const busca = useValorAtrasado(buscaDigitada)

  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, busca, nivelDeBlindagem, ativo }),
    [pagina, busca, nivelDeBlindagem, ativo],
  )

  const consulta = useCategorias(filtros)
  const remover = useRemoverCategoria()
  const removendo = remover.isPending

  const temFiltro = busca.length > 0 || nivelDeBlindagem !== '' || ativo !== ''

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirEdicao(categoria: Categoria): void {
    setEmEdicao(categoria)
    setDialogoAberto(true)
  }

  function limparFiltros(): void {
    setBuscaDigitada('')
    setNivelDeBlindagem('')
    setAtivo('')
    setPagina(1)
  }

  function tirarDoCatalogo(categoria: Categoria): void {
    remover.mutate(categoria.id, {
      onSuccess: () => {
        toast.success(`${categoria.nome} saiu do catálogo`, {
          description: 'A categoria some da vitrine e do app. A frota vinculada continua intacta.',
        })
      },
      onError: (causa: unknown) => {
        // A mensagem é a do SERVIDOR de propósito: no 409 `CATEGORIA_EM_USO`
        // ela diz quantos veículos impedem a remoção e qual é o caminho certo.
        // Só o servidor sabe esse número — reescrever aqui seria substituir uma
        // instrução exata por um palpite.
        const emUso = ApiError.is(causa) && causa.code === 'CATEGORIA_EM_USO'

        toast.error('Não foi possível tirar do catálogo', {
          description: errorMessage(causa),
          ...(emUso && podeEditar
            ? {
                action: {
                  label: 'Editar',
                  onClick: () => {
                    abrirEdicao(categoria)
                  },
                },
              }
            : {}),
        })
      },
    })
  }

  const colunas = useMemo<ColumnDef<Categoria>[]>(
    () => [
      {
        id: 'nome',
        header: 'Categoria',
        accessorFn: (linha: Categoria) => linha.nome,
        cell: ({ row }) => {
          const categoria: Categoria = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{categoria.nome}</p>
              <p className="truncate text-xs text-fg-muted">/{categoria.slug}</p>
            </div>
          )
        },
      },
      {
        id: 'modelosRepresentativos',
        header: 'Modelos representativos',
        accessorFn: (linha: Categoria) => linha.modelosRepresentativos,
        cell: ({ row }) => {
          const categoria: Categoria = row.original

          return (
            // A promessa da classe, com peso de citação: é o texto que o cliente
            // lê no app, e o único desta tabela que não fala com a operação.
            <p
              className="max-w-56 min-w-0 truncate border-l-2 border-border-strong pl-2 text-sm
                text-foreground"
              title={categoria.modelosRepresentativos}
            >
              {categoria.modelosRepresentativos}
            </p>
          )
        },
      },
      {
        id: 'nivelDeBlindagem',
        header: 'Blindagem',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeNivelDeBlindagem nivel={row.original.nivelDeBlindagem} />,
      },
      {
        id: 'configuracao',
        header: 'Configuração',
        enableSorting: false,
        cell: ({ row }) => {
          const categoria: Categoria = row.original

          return (
            <div className="whitespace-nowrap">
              <p className="tabular-money text-sm text-foreground">
                {categoria.lugares} lugares · {categoria.portas} portas
              </p>
              <p className="tabular-money text-xs text-fg-secondary">
                {categoria.malas} malas · {ROTULO_DO_CAMBIO[categoria.cambio]}
              </p>
            </div>
          )
        },
      },
      {
        id: 'caucaoBase',
        header: 'Caução',
        accessorFn: (linha: Categoria) => linha.caucaoBase,
        sortingFn: (a, b) => compararDinheiroDoWire(a.original.caucaoBase, b.original.caucaoBase),
        cell: ({ row }) => (
          <span className="tabular-money whitespace-nowrap text-sm font-medium text-foreground">
            {formatarMoedaDoWire(row.original.caucaoBase)}
          </span>
        ),
      },
      {
        id: 'kmDiarioIncluso',
        header: 'Km incluso',
        accessorFn: (linha: Categoria) => linha.kmDiarioIncluso,
        cell: ({ row }) => (
          <span className="tabular-money whitespace-nowrap text-sm text-fg-secondary">
            {row.original.kmDiarioIncluso} km/dia
          </span>
        ),
      },
      {
        id: 'valorDoKmExcedente',
        header: 'Km excedente',
        accessorFn: (linha: Categoria) => linha.valorDoKmExcedente,
        sortingFn: (a, b) =>
          compararDinheiroDoWire(a.original.valorDoKmExcedente, b.original.valorDoKmExcedente),
        cell: ({ row }) => (
          <span className="tabular-money whitespace-nowrap text-sm text-fg-secondary">
            {formatarMoedaDoWire(row.original.valorDoKmExcedente)}/km
          </span>
        ),
      },
      {
        id: 'veiculos',
        header: 'Frota',
        accessorFn: (linha: Categoria) => linha.veiculos,
        cell: ({ row }) => {
          const categoria: Categoria = row.original

          return (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Ver a frota de ${categoria.nome}`}
              onClick={() => {
                setCategoriaDaFrota(categoria)
              }}
            >
              <Car aria-hidden="true" />
              <span className="tabular-money">{categoria.veiculos}</span>
            </Button>
          )
        },
      },
      {
        id: 'ordemDeExibicao',
        header: 'Ordem',
        accessorFn: (linha: Categoria) => linha.ordemDeExibicao,
        cell: ({ row }) => (
          <span className="tabular-money text-sm text-fg-secondary">
            {row.original.ordemDeExibicao}
          </span>
        ),
      },
      {
        id: 'ativo',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeCatalogo ativo={row.original.ativo} />,
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const categoria: Categoria = row.original

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações de ${categoria.nome}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={() => {
                    setCategoriaDaFrota(categoria)
                  }}
                >
                  <Car aria-hidden="true" />
                  Frota da categoria
                </DropdownMenuItem>

                {podeEditar ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      abrirEdicao(categoria)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Editar categoria
                  </DropdownMenuItem>
                ) : null}

                {podeRemover && categoria.ativo ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      // Uma remoção por vez. O menu pode ser reaberto enquanto o
                      // DELETE corre, e sem isto o segundo clique dispararia
                      // outra requisição e um segundo toast sobre uma categoria
                      // que já saiu do catálogo.
                      disabled={removendo}
                      onSelect={() => {
                        tirarDoCatalogo(categoria)
                      }}
                    >
                      <CircleSlash aria-hidden="true" />
                      Tirar do catálogo
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas são as permissões e a remoção em curso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeRemover, removendo],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Categorias</h1>
          <p className="text-sm text-fg-secondary">
            É o que o cliente reserva — a classe, não o carro. Modelo e placa entram só na alocação.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Nova categoria
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="filtro-busca-categorias">Buscar</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                text-fg-muted"
            />
            <Input
              id="filtro-busca-categorias"
              type="search"
              value={buscaDigitada}
              onChange={(evento) => {
                setPagina(1)
                setBuscaDigitada(evento.target.value)
              }}
              placeholder="Nome, slug ou modelo de referência"
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-blindagem">Blindagem</Label>
          <Select
            id="filtro-blindagem"
            value={nivelDeBlindagem}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setNivelDeBlindagem(NIVEIS_DE_BLINDAGEM.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos os níveis</option>
            {NIVEIS_DE_BLINDAGEM.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_NIVEL_DE_BLINDAGEM[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-ativo-categorias">Situação</Label>
          <Select
            id="filtro-ativo-categorias"
            value={ativo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setAtivo(OPCOES_DE_ATIVO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            {OPCOES_DE_ATIVO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_CATALOGO[item]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Categorias do catálogo"
        estadoVazio={
          <EmptyState
            icone={Layers}
            titulo={
              temFiltro ? 'Nenhuma categoria com esses filtros' : 'Nenhuma categoria cadastrada'
            }
            descricao={
              temFiltro
                ? 'Ajuste a busca ou os filtros para ver outros resultados.'
                : 'Cadastre a primeira classe do catálogo. O cliente reserva a categoria; o veículo específico é escolhido na alocação.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirCriacao}>
                  <Plus aria-hidden="true" />
                  Nova categoria
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeCategoria
        aberto={dialogoAberto}
        categoria={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />

      <DialogoDaFrotaDaCategoria
        categoria={categoriaDaFrota}
        aoFechar={() => {
          setCategoriaDaFrota(null)
        }}
      />
    </div>
  )
}
